import React from 'react';
import { Channel, PresenceStatus, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { Megaphone, Users, MessageCircle, Bookmark, Pin, BellOff } from 'lucide-react';
import { useLongPress } from '../hooks/useLongPress';
import { PresenceDot } from './PresenceDot';

// 목록 미리보기용 상대 시간 (오늘=시각, 어제, 그 외=월/일)
export const formatRelativeTime = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSameDay(date, now)) {
    return new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' }).format(date);
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(date, yesterday)) return '어제';
  return new Intl.DateTimeFormat('ko-KR', { month: 'numeric', day: 'numeric' }).format(date);
};

// 채널 타입별 원형 아이콘 (색 배경 + 아이콘)
export const ChannelTypeIcon: React.FC<{ type: Channel['type']; size?: 'sm' | 'md' | 'lg' }> = ({ type, size = 'md' }) => {
  const circle = size === 'lg' ? 'w-12 h-12' : size === 'md' ? 'w-10 h-10' : 'w-9 h-9';
  const iconSize = size === 'lg' ? 22 : size === 'md' ? 19 : 17;
  const byType = {
    public: { bg: 'bg-amber-50', icon: <Megaphone size={iconSize} className="text-amber-600" /> },
    group: { bg: 'bg-emerald-50', icon: <Users size={iconSize} className="text-emerald-500" /> },
    self: { bg: 'bg-slate-100', icon: <Bookmark size={iconSize} className="text-slate-400" /> },
    dm: { bg: 'bg-blue-50', icon: <MessageCircle size={iconSize} className="text-blue-500" /> },
    private: { bg: 'bg-blue-50', icon: <MessageCircle size={iconSize} className="text-blue-500" /> },
  } as const;
  const { bg, icon } = byType[type] ?? byType.dm;
  return <span className={`${circle} ${bg} rounded-full flex items-center justify-center shrink-0`}>{icon}</span>;
};

export const unreadBadge = (count: number) =>
  count > 0 ? (
    <span className="bg-brand-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shrink-0">
      {count > 99 ? '99+' : count}
    </span>
  ) : null;

export const channelDisplayName = (ch: Channel) =>
  ch.type === 'self' ? '나와의 채팅' : (ch.name || '이름 없는 채널');

interface ChannelListItemProps {
  channel: Channel;
  unreadCount: number;
  onSelect: (channel: Channel) => void;
  /** 롱프레스 시 (채널, 터치 좌표) — 고정/뮤트 액션 메뉴 오픈용. 미전달 시 롱프레스 비활성 */
  onLongPress?: (channel: Channel, pos: { x: number; y: number }) => void;
  /** 데스크톱 우클릭 시 (채널, 마우스 좌표) — 고정/뮤트 액션 메뉴 오픈용. 미전달 시 브라우저 기본 메뉴 */
  onContextMenu?: (channel: Channel, pos: { x: number; y: number }) => void;
  /** lg=대화방 목록(48px 아바타), md=홈 카드(40px) */
  size?: 'md' | 'lg';
  /**
   * DM 상대의 접속 상태 (아바타 우하단 점). undefined면 점을 그리지 않는다.
   * 예전엔 boolean(isOnline)이어서 away/busy가 전부 초록 점으로 보여 자리비움을 구분할 수 없었다.
   */
  presenceStatus?: PresenceStatus;
  /** 데스크톱 목록에서 현재 열린 채널 하이라이트 */
  isActive?: boolean;
}

/**
 * 카카오톡식 대화방 목록 행: 아바타/아이콘 + (이름·시간) + (최근 메시지·안읽음 뱃지) 2줄.
 * DM은 상대방 실제 프로필 사진(dmPartnerAvatar), 그 외는 타입별 컬러 아이콘.
 */
export const ChannelListItem: React.FC<ChannelListItemProps> = ({
  channel: ch,
  unreadCount,
  onSelect,
  onLongPress,
  onContextMenu,
  size = 'md',
  presenceStatus,
  isActive = false,
}) => {
  const { handlers } = useLongPress((pos) => {
    onLongPress?.(ch, pos);
  });
  const longPressHandlers: Partial<typeof handlers> = onLongPress ? handlers : {};
  const avatarClass = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';

  return (
    <button
      onClick={() => onSelect(ch)}
      {...longPressHandlers}
      onContextMenu={(e) => {
        // useLongPress의 onContextMenu(터치 제스처 중 네이티브 메뉴 억제)를 먼저 태운다 —
        // preventDefault 되었으면 터치 경로이므로 데스크톱 우클릭 메뉴를 열지 않는다
        longPressHandlers.onContextMenu?.(e);
        if (e.defaultPrevented || !onContextMenu) return;
        e.preventDefault();
        onContextMenu(ch, { x: e.clientX, y: e.clientY });
      }}
      className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left touch-callout-none ${
        isActive ? 'bg-brand-100' : 'hover:bg-slate-50 active:bg-slate-100'
      }`}
    >
      {ch.type === 'dm' ? (
        <span className="relative shrink-0">
          <img
            src={resolveAvatarUrl(ch.dmPartnerAvatar)} onError={handleAvatarError}
            alt={channelDisplayName(ch)}
            loading="lazy"
            decoding="async"
            className={`${avatarClass} rounded-full object-cover bg-slate-100`}
          />
          {presenceStatus && (
            <PresenceDot
              status={presenceStatus}
              className="absolute bottom-0 right-0 w-3 h-3 border-2 border-white"
            />
          )}
        </span>
      ) : (
        <ChannelTypeIcon type={ch.type} size={size} />
      )}
      <span className="flex-1 min-w-0">
        <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 min-w-0">
            <span className="text-sm font-bold text-slate-800 truncate">{channelDisplayName(ch)}</span>
            {ch.type === 'group' && (ch.memberCount || 0) > 2 && (
              <span className="text-xs text-slate-400 shrink-0">{ch.memberCount}</span>
            )}
            {/* 상태 아이콘: slate-300은 흰 바탕에서 묻혀 "표시가 없다"는 신고가 있었다.
                고정은 홈 '즐겨찾기' 섹션 제목과 같은 brand-600, 알림끔은 중성 slate-500. */}
            {ch.isMuted && (
              <BellOff size={12} strokeWidth={2.25} className="text-slate-500 shrink-0" aria-label="알림 끔" />
            )}
            {ch.isPinned && (
              <Pin size={12} strokeWidth={2.25} className="text-brand-600 shrink-0" aria-label="고정됨" />
            )}
          </span>
          <span className="text-xs text-slate-400 shrink-0">{formatRelativeTime(ch.lastMessageAt)}</span>
        </span>
        <span className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-sm text-slate-500 truncate">{ch.lastMessage || ' '}</span>
          {unreadBadge(unreadCount)}
        </span>
      </span>
    </button>
  );
};
